import { DataCenter } from 'cio/models/account';

import { AuthenticationType } from 'cio/models/mail-server';

export enum EspKey {
  Mailgun = 'mailgun',
  Mailjet = 'mailjet',
  Mandrill = 'mandrill',
  Oracle = 'oracle',
  Sendgrid = 'sendgrid',
  Sparkpost = 'sparkpost',
  Postmark = 'postmark',
  Other = 'other',
}

export enum EspName {
  Mailgun = 'Mailgun',
  Mailjet = 'Mailjet',
  Mandrill = 'Mandrill',
  Oracle = 'Oracle DYN',
  Sendgrid = 'SendGrid',
  Sparkpost = 'SparkPost',
  Postmark = 'Postmark',
  Other = 'Other SMTP',
}

export interface EspInfo {
  name: EspName;
  logoUrl: string;
  supportsWebhooks: boolean;
  address?: string | { default: string; [key: string]: string };
  port?: string;
  authentication?: AuthenticationType;
  documentationUrl?: string;
  customXHeader?: string;
  webhookTrackingUrl?: Record<DataCenter, string>;
  bouncePostbackUrl?: Record<DataCenter, string>;
  spamComplaintPostbackUrl?: Record<DataCenter, string>;
}

export const ESP_AUTHENTICATION_OPTIONS = Object.freeze([
  { label: 'None', value: 'none' },
  { label: 'Plain', value: 'plain' },
  { label: 'Login', value: 'login' },
  { label: 'CRAM-MD5', value: 'cram_md5' },
]);

export const ESPS: Record<EspKey, EspInfo> = {
  mailgun: {
    name: EspName.Mailgun,
    logoUrl: new URL('cio/assets/images/email-service-providers/mailgun.png', import.meta.url).toString(),
    documentationUrl: 'https://customer.io/docs/triggered-lifecycle-email-with-mailgun',
    supportsWebhooks: true,
    address: {
      default: 'smtp.mailgun.org',
      eu: 'smtp.eu.mailgun.org',
    },
    port: '2525',
    authentication: 'login',
    webhookTrackingUrl: {
      us: 'https://track.customer.io/mailgun/events',
      eu: 'https://track-eu.customer.io/mailgun/events',
    },
  },
  mailjet: {
    name: EspName.Mailjet,
    logoUrl: new URL('cio/assets/images/email-service-providers/mailjet.png', import.meta.url).toString(),
    documentationUrl: 'https://customer.io/docs/triggered-lifecycle-email-with-mailjet',
    supportsWebhooks: true,
    address: 'in-v3.mailjet.com',
    port: '587',
    authentication: 'login',
    webhookTrackingUrl: {
      us: 'https://track.customer.io/mailjet/events',
      eu: 'https://track-eu.customer.io/mailjet/events',
    },
  },
  mandrill: {
    name: EspName.Mandrill,
    logoUrl: new URL('cio/assets/images/email-service-providers/mandrill.png', import.meta.url).toString(),
    documentationUrl: 'https://customer.io/docs/triggered-lifecycle-email-with-mandrill',
    supportsWebhooks: true,
    /* eslint-disable spellcheck/spell-checker */
    address: 'smtp.mandrillapp.com',
    port: '2525',
    authentication: 'login',
    webhookTrackingUrl: {
      us: 'https://track.customer.io/mandrill/events',
      eu: 'https://track-eu.customer.io/mandrill/events',
    },
  },
  oracle: {
    name: EspName.Oracle,
    logoUrl: new URL('cio/assets/images/email-service-providers/oracle.png', import.meta.url).toString(),
    documentationUrl: 'https://customer.io/docs/triggered-lifecycle-email-with-oracle-dyn',
    supportsWebhooks: true,
    address: 'smtp.email.dynect.net',
    port: '587',
    authentication: 'login',
    customXHeader: 'X-CIO-Email-ID',
    bouncePostbackUrl: {
      us: 'https://track.customer.io/oracledyn/events?event=bounced&dyn_cio_email_id=@X-CIO-Email-ID&email=@email&bounce_type=@bouncetype&reason=@bouncerule',
      eu: 'https://track-eu.customer.io/oracledyn/events?event=bounced&dyn_cio_email_id=@X-CIO-Email-ID&email=@email&bounce_type=@bouncetype&reason=@bouncerule',
    },
    spamComplaintPostbackUrl: {
      us: 'https://track.customer.io/oracledyn/events?event=spammed&dyn_cio_email_id=@X-CIO-Email-ID&email=@email',
      eu: 'https://track-eu.customer.io/oracledyn/events?event=spammed&dyn_cio_email_id=@X-CIO-Email-ID&email=@email',
    },
  },
  sendgrid: {
    name: EspName.Sendgrid,
    logoUrl: new URL('cio/assets/images/email-service-providers/sendgrid.png', import.meta.url).toString(),
    documentationUrl: 'https://customer.io/docs/triggered-lifecycle-email-with-sendgrid',
    supportsWebhooks: true,
    address: 'sendgrid',
    webhookTrackingUrl: {
      us: 'https://track.customer.io/sendgrid/events',
      eu: 'https://track-eu.customer.io/sendgrid/events',
    },
  },
  sparkpost: {
    name: EspName.Sparkpost,
    logoUrl: new URL('cio/assets/images/email-service-providers/sparkpost.png', import.meta.url).toString(),
    documentationUrl: 'https://customer.io/docs/triggered-lifecycle-email-with-sparkpost',
    supportsWebhooks: true,
    /* eslint-disable spellcheck/spell-checker */
    address: 'smtp.sparkpostmail.com',
    port: '587',
    authentication: 'login',
    webhookTrackingUrl: {
      us: 'https://track.customer.io/sparkpost/events',
      eu: 'https://track-eu.customer.io/sparkpost/events',
    },
  },
  postmark: {
    name: EspName.Postmark,
    logoUrl: new URL('cio/assets/images/email-service-providers/postmark.png', import.meta.url).toString(),
    documentationUrl: 'https://customer.io/docs/triggered-lifecycle-email-with-postmark/',
    supportsWebhooks: true,
    /* eslint-disable spellcheck/spell-checker */
    address: 'smtp.postmarkapp.com',
    port: '587',
    authentication: 'login',
    webhookTrackingUrl: {
      us: 'https://track.customer.io/postmark/events',
      eu: 'https://track-eu.customer.io/postmark/events',
    },
  },
  other: {
    name: EspName.Other,
    logoUrl: new URL('cio/assets/images/sending-airplane.svg', import.meta.url).toString(),
    supportsWebhooks: false,
    authentication: 'none',
  },
};

export default function espInfo(key?: EspKey) {
  return key != null ? ESPS[key] ?? ESPS.other : ESPS.other;
}
